import {
    APP_BASE_HREF,
    PlatformLocation,
    registerLocaleData,
} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEl from '@angular/common/locales/el';
import localeEs from '@angular/common/locales/es';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeElExtra from '@angular/common/locales/extra/el';
import localeEsExtra from '@angular/common/locales/extra/es';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeItExtra from '@angular/common/locales/extra/it';
import localePtExtra from '@angular/common/locales/extra/pt';
import localeTrExtra from '@angular/common/locales/extra/tr';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import localeTr from '@angular/common/locales/tr';
import {
    inject,
    NgModule,
    provideAppInitializer,
    provideEnvironmentInitializer,
    provideZonelessChangeDetection,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDateFnsModule } from '@angular/material-date-fns-adapter';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { provideShellTemporaryLinkConfig } from '@intaracta/data-access-shell-temporary-link';
import { AuthEffects } from '@interacta-shared/data-access-auth';
import { PageTitleEffects } from '@interacta-shared/data-access-common';
import {
    ConfigurationService,
    provideEnvironment,
    provideLanguageConfig,
} from '@interacta-shared/data-access-configuration';
import {
    ErrorService,
    provideErrorHandler,
    provideErrorPageInfoRegistry,
} from '@interacta-shared/data-access-error';
import {
    NotificationEffects,
    provideNotificationConfig,
} from '@interacta-shared/data-access-notification';
import { ThemeEffects } from '@interacta-shared/data-access-theme';
import {
    BoundedTipEffects,
    provideAttachment,
    provideUserProfileDialog,
    UserProfileDialogComponent,
} from '@interacta-shared/feature-caronte';
import { LinksDialogComponent } from '@interacta-shared/feature-external-links';
import { LicenseUpgradeDialogComponent } from '@interacta-shared/feature-license';
import {
    GroupInfoDialogComponent,
    provideGroupInfoDialog,
} from '@interacta-shared/feature-member';
import { MobileBannerComponent } from '@interacta-shared/feature-mobile-banner';
import { PrivateEmailEffects } from '@interacta-shared/feature-private-email';
import { registerQuillTableUp } from '@interacta-shared/feature-quill';
import {
    OverlayTipsComponent,
    TipsEffects,
    TipService,
} from '@interacta-shared/feature-tip';
import {
    ConfirmDialogComponent,
    HorizontalPanelResizerComponent,
    LayerComponent,
    LoadingLayerComponent,
} from '@interacta-shared/ui-common';
import { IconComponent } from '@interacta-shared/ui-icons';
import {
    AppLogoPipe,
    registerQuillPlainClipboard,
} from '@interacta-shared/util-common';
import { AICreditEffects } from '@interacta/data-access-ai';
import {
    AFTER_COMMUNITY_TREE_INITIALIZED_TOKEN,
    COMMUNITIES_SERVICE_TOKEN,
} from '@interacta/data-access-community-tree';
import { PostAttachmentService } from '@interacta/data-access-post';
import { BottomMenuComponent } from '@interacta/feature-bottom-menu';
import {
    BulkWorkflowTransitionBannerComponent,
    BulkWorkflowTransitionEffects,
} from '@interacta/feature-bulk-workflow-transition';
import { ToastNotificationOverlayComponent } from '@interacta/feature-toast-notification';
import { AIConvoSidePanelComponent } from '@modules/ai-convo/components/ai-convo-side-panel/ai-convo-side-panel.component';
import { AIConvoEffects } from '@modules/ai-convo/store/ai-convo.effects';
import { AIModule } from '@modules/ai/ai.module';
import { AppRoutingModule } from '@modules/app-routing/app-routing.module';
import { AttachmentsVersionsDialogComponent } from '@modules/attachments/components/attachments-versions-dialog/attachments-versions-dialog.component';
import { CarouselContainerComponent } from '@modules/carousel/components/carousel-container/carousel-container.component';
import { ChatModule } from '@modules/chat/chat.module';
import { CollectionsDialogComponent } from '@modules/collections-dialog/components/collections-dialog/collections-dialog.component';
import { CommunitiesModule } from '@modules/communities/communities.module';
import { registerShellCustomMetadataAdapters } from '@modules/communities/models/custom-metadata/custom-metadata-adapters.provider';
import { CommunitiesService } from '@modules/communities/services/communities.service';
import { fetchQuickFilters } from '@modules/communities/store/community/community.actions';
import { fetchHomeDefinitions } from '@modules/communities/store/home-pages/home-pages.actions';
import { CoreModule } from '@modules/core/core.module';
import { getBaseHref } from '@modules/core/helpers/base-href.utils';
import { customStoreDevtoolsSerializer } from '@modules/core/helpers/delta/custom-store-dev-tool-serializer';
import { isStoreDevtoolsEnabled } from '@modules/core/helpers/store-devtools.utils';
import { translateHttpLoaderFactory } from '@modules/core/helpers/translate-http-loader-factory';
import { errorPageInfoRegistryConfig } from '@modules/core/models/error-page-info.model';
import { BootService } from '@modules/core/services/boot.service';
import { CoreGroupInfoDialogService } from '@modules/core/services/core-group-info-dialog.service';
import { CoreUserProfileDialogService } from '@modules/core/services/core-user-profile-dialog.service';
import { appMetaReducers, AppState } from '@modules/core/store';
import { UiEffects } from '@modules/core/store/UI/ui.effects';
import {
    AppRouterSerializer,
    AppRouterState,
} from '@modules/core/store/app-router.serializer';
import { AppEffects } from '@modules/core/store/app.effects';
import { reducers } from '@modules/core/store/app.reducer';
import { UserCapabilitiesEffects } from '@modules/core/store/user-capabilities/user-capabilities.effetcs';
import { HashtagInfoDialogComponent } from '@modules/hashtag-info/components/hashtag-info-dialog/hashtag-info-dialog.component';
import { provideHashtagInfoStore } from '@modules/hashtag-info/hashtag-info.provider';
import { AppInfoDialogComponent } from '@modules/header/components/app-info-dialog/app-info-dialog.component';
import { HeaderComponent } from '@modules/header/components/header/header.component';
import { ILabInteropEffects } from '@modules/i-lab-interop/store/i-lab-interop.effects';
import { LightBoxFooterComponent } from '@modules/light-box/components/light-box-footer/light-box-footer.component';
import { LightBoxComponent } from '@modules/light-box/components/light-box/light-box.component';
import { SectionsEffects } from '@modules/light-box/store/sections/sections.effects';
import { sectionsReducer } from '@modules/light-box/store/sections/sections.reducer';
import { MentionsModule } from '@modules/mentions/mentions.module';
import { notificationConfigFactory } from '@modules/notification/utils/shell-notification-config.util';
import { CommentFullscreenEditorComponent } from '@modules/post-comment/components/comment-fullscreen-editor/comment-fullscreen-editor.component';
import { RelationsDialogComponent } from '@modules/relations-dialog/components/relations-dialog/relations-dialog.component';
import { provideRelationDialogStore } from '@modules/relations-dialog/relation-dialog.provider';
import { SharedV2Module } from '@modules/shared-v2/shared-v2.module';
import { CustomReuseStrategy } from '@modules/shared-v2/utils/route-reuse';
import { SidebarComponent } from '@modules/sidebar/components/sidebar/sidebar.component';
import { SkipLinksComponent } from '@modules/skip-links/skip-links/skip-links.component';
import { StateModule } from '@modules/state/state.module';
import { TableInfoDialogComponent } from '@modules/table-info/components/table-info-dialog/table-info-dialog.component';
import { provideTableInfoStore } from '@modules/table-info/table-info.provider';
import { UploadEffects } from '@modules/upload-dialog/store/upload.effects';
import { UploadDialogModule } from '@modules/upload-dialog/upload-dialog.module';
import { EffectsModule } from '@ngrx/effects';
import {
    NavigationActionTiming,
    StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
    provideTranslateLoader,
    provideTranslateService,
} from '@ngx-translate/core';
import { appEnvironment } from 'environments/environment';
import { QuillModule } from 'ngx-quill';
import { AppComponent } from './app.component';

registerLocaleData(localeIt, 'it', localeItExtra);
registerLocaleData(localeEs, 'es', localeEsExtra);
registerLocaleData(localeDe, 'de', localeDeExtra);
registerLocaleData(localeEl, 'el', localeElExtra);
registerLocaleData(localePt, 'pt', localePtExtra);
registerLocaleData(localeFr, 'fr', localeFrExtra);
registerLocaleData(localeTr, 'tr', localeTrExtra);

function initInteractaService(
    bootService: BootService,
    configurationService: ConfigurationService,
    errorService: ErrorService,
) {
    return (): Promise<any> =>
        bootService.initApp().catch((e) => {
            configurationService.initTranslateLang();
            errorService.handle(e);
        });
}

const standaloneComponents = [
    MobileBannerComponent,
    LayerComponent,
    BulkWorkflowTransitionBannerComponent,
    ConfirmDialogComponent,
    LicenseUpgradeDialogComponent,
    BottomMenuComponent,
    OverlayTipsComponent,
    ToastNotificationOverlayComponent,
    LoadingLayerComponent,
    SkipLinksComponent,
    AIConvoSidePanelComponent,
    IconComponent,
    UserProfileDialogComponent,
    CollectionsDialogComponent,
    GroupInfoDialogComponent,
    RelationsDialogComponent,
    AppInfoDialogComponent,
    HorizontalPanelResizerComponent,
    LinksDialogComponent,
    SidebarComponent,
    CarouselContainerComponent,
    TableInfoDialogComponent,
    AttachmentsVersionsDialogComponent,
    CommentFullscreenEditorComponent,
    LightBoxComponent,
    LightBoxFooterComponent,
    HeaderComponent,
    HashtagInfoDialogComponent,
];

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        ReactiveFormsModule,
        //CoreModule have to init here before others Modules
        //because it has a custom error handler which must run at the beginning
        CoreModule.forRoot(),
        StateModule,
        SharedV2Module,
        QuillModule.forRoot(),
        RouterModule,
        AppRoutingModule,
        StoreModule.forRoot<AppState>(reducers, {
            metaReducers: appMetaReducers,
            runtimeChecks: {
                strictActionImmutability: false,
                strictStateImmutability: false,
                strictActionTypeUniqueness: true,
            },
        }),
        EffectsModule.forRoot([
            AuthEffects,
            AppEffects,
            UiEffects,
            UploadEffects,
            TipsEffects,
            PageTitleEffects<AppRouterState>,
            BoundedTipEffects,
            UserCapabilitiesEffects,
            PrivateEmailEffects,
            ThemeEffects,
            BulkWorkflowTransitionEffects,
            AICreditEffects,
            AIConvoEffects,
            NotificationEffects,
            ILabInteropEffects,
        ]),
        StoreRouterConnectingModule.forRoot({
            serializer: AppRouterSerializer,
            navigationActionTiming: NavigationActionTiming.PostActivation,
        }),
        ChatModule,
        MentionsModule,
        StoreModule.forFeature('sections', sectionsReducer),
        EffectsModule.forFeature([SectionsEffects]),
        UploadDialogModule,
        CommunitiesModule.forRoot(),
        AIModule,
        MatDateFnsModule,
        AppLogoPipe,
        ...standaloneComponents,
    ],
    providers: [
        provideTranslateService({
            loader: provideTranslateLoader(translateHttpLoaderFactory),
        }),
        provideEnvironmentInitializer(() => registerQuillPlainClipboard()),
        provideEnvironmentInitializer(() => registerQuillTableUp()),
        provideZonelessChangeDetection(),
        provideEnvironment(appEnvironment),
        provideErrorPageInfoRegistry({
            ...errorPageInfoRegistryConfig,
        }),
        provideErrorHandler(TipService),
        provideAppInitializer(() => {
            registerShellCustomMetadataAdapters();
            const initializerFn = initInteractaService(
                inject(BootService),
                inject(ConfigurationService),
                inject(ErrorService),
            );
            return initializerFn();
        }),
        provideNotificationConfig(notificationConfigFactory),
        provideShellTemporaryLinkConfig(),
        ...(isStoreDevtoolsEnabled()
            ? [
                  provideStoreDevtools({
                      maxAge: 50,
                      serialize: {
                          replacer: customStoreDevtoolsSerializer,
                      },
                  }),
              ]
            : []),

        { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
        {
            provide: APP_BASE_HREF,
            useFactory: getBaseHref,
            deps: [PlatformLocation],
        },
        // profile providers
        provideUserProfileDialog(CoreUserProfileDialogService),
        provideGroupInfoDialog(CoreGroupInfoDialogService),
        provideAttachment(PostAttachmentService),
        provideRelationDialogStore(),
        provideTableInfoStore(),
        provideHashtagInfoStore(),
        provideLanguageConfig(),
        //community-tree providers
        {
            provide: COMMUNITIES_SERVICE_TOKEN,
            useExisting: CommunitiesService,
        },
        {
            provide: AFTER_COMMUNITY_TREE_INITIALIZED_TOKEN,
            useFactory: (store: Store) => () => {
                store.dispatch(fetchQuickFilters());
                store.dispatch(fetchHomeDefinitions({ force: false }));
            },
            deps: [Store],
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
